import { useMemo } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { useSettingsContext } from 'src/context/theme-settings-context';
import { BREAKPOINTS } from 'src/theme/breakpoints';
import { componentsOverrides } from 'src/theme/overrides';

import { createPresets } from './options/presets';
// options
import { customShadows } from './custom-shadows';
// system
import { palette, shadesForMode } from './palette';
import { shadows } from './shadows';
import { TYPOGRAPHY } from './typography';

// ----------------------------------------------------------------------

declare module '@mui/system/createTheme/shape' {
  interface Shape {
    borderRadius: number;
    borderRadiusLg: number;
  }
}

type Props = {
  children: React.ReactNode;
};

export function ThemeProvider({ children }: Props) {
  const { themeMode = 'light' } = useSettingsContext();

  const presets = createPresets('default');

  const memoizedValue = useMemo(
    () => ({
      palette: {
        ...palette(themeMode),
        primary: shadesForMode(themeMode, presets.palette.primary),
      },
      customShadows: {
        ...customShadows(themeMode),
        ...presets.customShadows,
      },
      shadows: shadows(themeMode),
      shape: { borderRadius: 8, borderRadiusLg: 16 },
      breakpoints: BREAKPOINTS,
      typography: TYPOGRAPHY,
    }),
    [themeMode, presets.palette, presets.customShadows]
  );

  const theme = createTheme(memoizedValue as ThemeOptions);
  theme.components = componentsOverrides(theme);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
